import * as React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import MetaAdsInsightsService from 'services/MetaAdsInsightsService';
import SettingsService from 'services/SettingsService';
import { LatestResponse, MetaAdsConfig, ProposedAction, Snapshot } from './types';
import { META_ADS_SETTINGS_NAMESPACE } from './settingsConfig';
import { buildAdTable, buildCampaignTable, buildTestingTable, SPEND_SORT } from './grids';
import AccountHealthCards from './components/AccountHealthCards';
import FilterBar, {
  anyFilterActive,
  EMPTY_FILTERS,
  matchesEntity,
  matchesVerdict,
  TableFilters,
} from './components/FilterBar';
import { actionTableMismatches } from './utils';
import MetaDataGrid from './components/MetaDataGrid';
import ProposedActions from './components/ProposedActions';
import SettingsDialog from './components/SettingsDialog';
import SnapshotHeader from './components/SnapshotHeader';

const APPROVER_EMAIL = 'kevin@sbly.com'; // single admin for v1

type Snack = { open: boolean; msg: string; sev: 'success' | 'error' };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function MetaAdsInsights() {
  const service = React.useMemo(() => new MetaAdsInsightsService(), []);
  const settingsService = React.useMemo(() => new SettingsService(), []);
  const [data, setData] = React.useState<LatestResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [pending, setPending] = React.useState<
    Record<string, 'approve' | 'reject' | undefined>
  >({});
  const [refreshing, setRefreshing] = React.useState(false);
  const [config, setConfig] = React.useState<MetaAdsConfig | null>(null);
  // One filter state per table — filtering one table never affects another.
  const [campaignFilters, setCampaignFilters] = React.useState<TableFilters>(EMPTY_FILTERS);
  // Winners defaults to Scale — keeping a winner is status quo; the actionable
  // question is where to raise budgets. The Keep chip is one click away.
  const [winnersFilters, setWinnersFilters] = React.useState<TableFilters>({
    ...EMPTY_FILTERS,
    verdict: ['scale'],
  });
  // Needs a Decision defaults to Pause + Promote — the verdicts that require a
  // move now. Monitor (borderline, keep watching) is one chip-click away.
  const [decisionsFilters, setDecisionsFilters] = React.useState<TableFilters>({
    ...EMPTY_FILTERS,
    verdict: ['pause', 'promote'],
  });
  const [testingFilters, setTestingFilters] = React.useState<TableFilters>(EMPTY_FILTERS);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [savingConfig, setSavingConfig] = React.useState(false);
  const [snack, setSnack] = React.useState<Snack>({
    open: false,
    msg: '',
    sev: 'success',
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    const resp = await service.getLatest();
    setData(resp);
    setLoading(false);
  }, [service]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Load the tunable settings (ad-spend cutoff + token budget) once on mount, from the
  // generic settings store (may be {} until something is saved — defaults fill the form).
  React.useEffect(() => {
    settingsService
      .get(META_ADS_SETTINGS_NAMESPACE)
      .then(c => c && setConfig(c as MetaAdsConfig));
  }, [settingsService]);

  // Refresh = request a fresh recompute, then poll until a newer snapshot lands.
  const handleRefresh = React.useCallback(async () => {
    if (refreshing) return;
    const prevGen = data?.snapshot?.generated_at;
    setRefreshing(true);
    const ok = await service.requestRefresh(APPROVER_EMAIL);
    if (!ok) {
      setSnack({ open: true, msg: 'Could not request refresh', sev: 'error' });
      setRefreshing(false);
      return;
    }
    setSnack({ open: true, msg: 'Refresh requested — fetching fresh data…', sev: 'success' });
    const deadline = Date.now() + 6 * 60 * 1000;
    const poll = async () => {
      const resp = await service.getLatest();
      if (resp?.snapshot && resp.snapshot.generated_at !== prevGen) {
        setData(resp);
        setRefreshing(false);
        setSnack({ open: true, msg: 'Updated with fresh data', sev: 'success' });
        return;
      }
      if (Date.now() > deadline) {
        setRefreshing(false);
        setSnack({ open: true, msg: 'Still processing — check back shortly', sev: 'success' });
        return;
      }
      window.setTimeout(poll, 15000);
    };
    window.setTimeout(poll, 15000);
  }, [refreshing, data, service]);

  // Save the tunable config, then kick a refresh so it takes effect on a fresh run.
  const handleSaveConfig = async (patch: {
    min_d7_spend: number;
    token_budget: number;
  }) => {
    setSavingConfig(true);
    const updated = await settingsService.update(META_ADS_SETTINGS_NAMESPACE, patch);
    setSavingConfig(false);
    if (!updated) {
      setSnack({ open: true, msg: 'Failed to save settings', sev: 'error' });
      return;
    }
    setConfig(prev => ({ ...(prev ?? {}), ...(updated as MetaAdsConfig) }));
    setSettingsOpen(false);
    setSnack({
      open: true,
      msg: 'Settings saved — refreshing with the new settings…',
      sev: 'success',
    });
    handleRefresh();
  };

  // Light focus-poll: while any action is awaiting execution, refresh action
  // status on window focus so the watcher flipping approved→executed shows up.
  React.useEffect(() => {
    const hasApproved = (data?.actions ?? []).some(a => a.status === 'approved');
    if (!hasApproved) return undefined;
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [data, load]);

  const snapshot: Snapshot | null = data?.snapshot ?? null;
  const actions: ProposedAction[] = data?.actions ?? [];

  const applyUpdate = (
    updated: ProposedAction | null,
    kind: 'approve' | 'reject',
  ) => {
    if (!updated) {
      setSnack({ open: true, msg: `Failed to ${kind} action`, sev: 'error' });
      return;
    }
    setData(prev =>
      prev
        ? {
            ...prev,
            actions: prev.actions.map(a =>
              a.action_id === updated.action_id ? updated : a,
            ),
          }
        : prev,
    );
    setSnack({
      open: true,
      msg: kind === 'approve' ? 'Action approved' : 'Action rejected',
      sev: 'success',
    });
  };

  const onApprove = async (a: ProposedAction) => {
    setPending(p => ({ ...p, [a.action_id]: 'approve' }));
    applyUpdate(await service.approveAction(a.action_id, APPROVER_EMAIL), 'approve');
    setPending(p => ({ ...p, [a.action_id]: undefined }));
  };
  const onReject = async (a: ProposedAction) => {
    setPending(p => ({ ...p, [a.action_id]: 'reject' }));
    applyUpdate(await service.rejectAction(a.action_id, APPROVER_EMAIL), 'reject');
    setPending(p => ({ ...p, [a.action_id]: undefined }));
  };

  const closeSnack = () => setSnack(s => ({ ...s, open: false }));

  if (loading && !data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!snapshot) {
    return (
      <Box sx={{ p: 3 }}>
        <SnapshotHeader snapshot={null} onRefresh={handleRefresh} loading={refreshing} />
        <Alert severity="warning">
          No snapshot yet — the agent hasn’t posted one. Numbers runs are hourly;
          full runs with written analysis are 4×/day (8am / 12 / 4 / 8pm PT).
        </Alert>
      </Box>
    );
  }

  // Each table filters independently on the snapshot arrays (so row counts,
  // sorting, and pinned columns stay coherent). Winners/decisions also filter on
  // their own verdict group.
  const campaign = buildCampaignTable(
    (snapshot.campaigns ?? []).filter(c =>
      matchesEntity(c.campaign_name, c.product, campaignFilters),
    ),
  );
  const winners = buildAdTable(
    (snapshot.winners ?? []).filter(
      a =>
        matchesEntity(a.campaign_name, a.product, winnersFilters) &&
        matchesVerdict(a.verdict, winnersFilters),
    ),
  );
  const decisions = buildAdTable(
    (snapshot.decisions ?? []).filter(
      a =>
        matchesEntity(a.campaign_name, a.product, decisionsFilters) &&
        matchesVerdict(a.verdict, decisionsFilters),
    ),
  );
  const testing = buildTestingTable(
    (snapshot.testing_past_3x ?? []).filter(t =>
      matchesEntity(t.campaign_name, t.product, testingFilters),
    ),
  );

  const proseStatus = snapshot.prose_status;
  const showBudgetWarning =
    !!proseStatus && !proseStatus.ok && proseStatus.reason === 'budget_exceeded';

  return (
    <Box sx={{ p: 3 }}>
      <SnapshotHeader
        snapshot={snapshot}
        onRefresh={handleRefresh}
        onSettings={() => setSettingsOpen(true)}
        loading={refreshing}
      />
      {showBudgetWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {proseStatus?.message ??
            'The written analysis exceeded its token budget and was truncated — showing the rule-based summary. Raise the token budget or the ad-spend cutoff in settings.'}
        </Alert>
      )}
      <AccountHealthCards health={snapshot.account_health} />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Each metric cell shows Today / Yesterday / 7d / 14d (today bold). Green = at/above target · red = out of range.
      </Typography>

      <Section title="Campaign Overview">
        <FilterBar filters={campaignFilters} onChange={setCampaignFilters} />
        <MetaDataGrid
          rows={campaign.rows}
          columns={campaign.columns}
          sortModel={SPEND_SORT}
          treeData
          getTreeDataPath={campaign.getTreeDataPath}
          groupingColDef={campaign.groupingColDef}
        />
      </Section>
      <Section title="Winners (keep / scale)">
        <FilterBar
          filters={winnersFilters}
          onChange={setWinnersFilters}
          verdictLabel="VERDICT"
          verdictOptions={[
            { value: 'keep', display: 'Keep' },
            { value: 'scale', display: 'Scale' },
          ]}
        />
        <MetaDataGrid rows={winners.rows} columns={winners.columns} sortModel={SPEND_SORT} />
      </Section>
      <Section title="Needs a Decision">
        <FilterBar
          filters={decisionsFilters}
          onChange={setDecisionsFilters}
          verdictLabel="VERDICT"
          verdictOptions={[
            { value: 'pause', display: 'Pause' },
            { value: 'monitor', display: 'Monitor' },
            { value: 'promote', display: 'Promote' },
          ]}
        />
        <MetaDataGrid rows={decisions.rows} columns={decisions.columns} sortModel={SPEND_SORT} />
      </Section>
      {(testing.rows.length > 0 || anyFilterActive(testingFilters)) && (
        <Section title="Testing ads past 3× CPA (lifetime)">
          <FilterBar filters={testingFilters} onChange={setTestingFilters} />
          <MetaDataGrid
            rows={testing.rows}
            columns={testing.columns}
            sortModel={[{ field: 'lifetime_spend', sort: 'desc' }]}
          />
        </Section>
      )}
      <Section title="Proposed Actions">
        <ProposedActions
          actions={actions}
          warnings={actionTableMismatches(actions, snapshot)}
          pending={pending}
          onApprove={onApprove}
          onReject={onReject}
        />
      </Section>

      <SettingsDialog
        open={settingsOpen}
        config={config}
        saving={savingConfig}
        onClose={() => setSettingsOpen(false)}
        onSave={handleSaveConfig}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={closeSnack}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
